import { apiCall } from "./api";

/**
 * Loader for the refer-a-friend band (`GET /api/referral`).
 *
 * The referral link is stable per customer, so one fetch per SPA session is
 * plenty — the promise is cached at module level and shared by every band
 * mount (the band remounts on each route change; it must not refetch).
 * Failures are NOT cached: a flaky first hit shouldn't pin the band to the
 * generic variant for the whole session.
 *
 * Only fetch for customer principals — anon visitors always resolve to
 * `referral: null` server-side, and skipping the call entirely is what keeps
 * the cache free of anon responses (so an anon→customer flip mid-session can
 * never serve a stale generic result). `clearReferralCache` is belt-and-braces
 * for that flip, called by the band when it observes the auth kind change.
 */

/**
 * The referral credit amount as shown in ALL customer-facing copy — share
 * modal rider, refer-a-friend band, invite dialog, and the shared-plan
 * receiving page. One constant so a Yotpo campaign change is a one-line edit.
 */
export const REFERRAL_CREDIT_LABEL = "$10";

export type ReferralInfo = {
  /** Absolute referral URL (server normalizes Yotpo's scheme-less links). */
  link: string;
  /** Short referral code shown as "Your code · ABC123"; null when Yotpo only mints links. */
  code: string | null;
};

export type ReferralResponse = {
  status: "customer" | "anon";
  referral: ReferralInfo | null;
};

let cached: Promise<ReferralResponse> | null = null;

export function loadReferral(): Promise<ReferralResponse> {
  if (!cached) {
    const promise = apiCall<ReferralResponse>("/api/referral").catch((err: unknown) => {
      // Drop only our own entry — a concurrent clear-and-reload shouldn't lose
      // its fresh promise to this stale rejection.
      if (cached === promise) cached = null;
      throw err;
    });
    cached = promise;
  }
  return cached;
}

export function clearReferralCache(): void {
  cached = null;
}
